import { Boxes } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

function LegalLayout({
  title,
  updatedAt,
  children,
}: {
  title: string;
  updatedAt: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-svh bg-background">
      <header className="border-b">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Boxes className="size-4" />
            </div>
            <span className="font-semibold tracking-tight">NeedfulThings</span>
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-semibold text-3xl tracking-tight">{title}</h1>
        <p className="mt-2 text-muted-foreground text-sm">Last updated: {updatedAt}</p>
        <div className="mt-10 space-y-8">{children}</div>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-4 px-6 py-8 text-muted-foreground text-sm">
          <span>© {new Date().getFullYear()} NeedfulThings. All rights reserved.</span>
          <nav className="flex gap-4">
            <Link to="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-foreground">
              Privacy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-semibold text-foreground text-lg">{title}</h2>
      <div className="space-y-3 text-muted-foreground text-sm leading-7">{children}</div>
    </section>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updatedAt="October 2026">
      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using the NeedfulThings admin dashboard (the "Service"), you agree to be
          bound by these Terms of Service. If you do not agree with any part of these terms, you
          must not access or use the Service.
        </p>
      </Section>

      <Section title="2. Accounts and Sign-In">
        <p>
          Access to the Service is granted through third-party authentication providers, currently
          Google. You are responsible for maintaining the confidentiality of your account and for
          all activity that occurs under it. You must notify us immediately of any unauthorized use
          of your account.
        </p>
      </Section>

      <Section title="3. Acceptable Use">
        <p>
          You agree not to misuse the Service. This includes, but is not limited to, attempting to
          access it using a method other than the interface and instructions we provide, disrupting
          or interfering with the Service or its infrastructure, or accessing data that you are not
          authorized to access.
        </p>
      </Section>

      <Section title="4. Content and Intellectual Property">
        <p>
          All content, trademarks, and data on the Service, including software, text, and graphics,
          are the property of NeedfulThings or its licensors and are protected by applicable
          intellectual property laws. You may not reproduce, distribute, or create derivative works
          without our prior written consent.
        </p>
      </Section>

      <Section title="5. Termination">
        <p>
          We may suspend or terminate your access to the Service at any time, without prior notice
          or liability, for any reason, including if you breach these Terms. Upon termination, your
          right to use the Service will immediately cease.
        </p>
      </Section>

      <Section title="6. Disclaimer of Warranties">
        <p>
          The Service is provided on an "as is" and "as available" basis without warranties of any
          kind, whether express or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>
      </Section>

      <Section title="7. Limitation of Liability">
        <p>
          In no event shall NeedfulThings be liable for any indirect, incidental, special,
          consequential, or punitive damages arising out of or related to your use of the Service,
          whether based on warranty, contract, tort, or any other legal theory.
        </p>
      </Section>

      <Section title="8. Changes to These Terms">
        <p>
          We reserve the right to modify these Terms at any time. We will provide notice of material
          changes by updating the "Last updated" date above. Your continued use of the Service after
          changes take effect constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section title="9. Contact">
        <p>
          If you have any questions about these Terms, please contact us at{' '}
          <a
            href="mailto:support@needfulthings.com"
            className="text-foreground underline underline-offset-4"
          >
            support@needfulthings.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updatedAt="October 2026">
      <Section title="1. Information We Collect">
        <p>
          When you sign in with Google, we receive basic profile information such as your name,
          email address, profile picture, and a provider-specific account identifier. We also store
          session data and, where applicable, information you create while using the Service.
        </p>
      </Section>

      <Section title="2. How We Use Information">
        <p>
          We use the information we collect to authenticate you, operate and maintain the Service,
          provide support, send service-related communications, and protect against unauthorized
          access or abuse.
        </p>
      </Section>

      <Section title="3. Cookies and Local Storage">
        <p>
          We use cookies and browser local storage to manage authentication sessions and to keep you
          signed in. Disabling these may prevent parts of the Service from functioning correctly.
        </p>
      </Section>

      <Section title="4. Third-Party Sign-In">
        <p>
          Authentication is handled by third-party providers, currently Google. Your use of those
          providers is governed by their own terms and privacy policies, and we encourage you to
          review them.
        </p>
      </Section>

      <Section title="5. Data Retention">
        <p>
          We retain personal information for as long as necessary to provide the Service and for
          legitimate business or legal purposes. When data is no longer needed, we delete or
          anonymize it.
        </p>
      </Section>

      <Section title="6. Security">
        <p>
          We take reasonable measures to protect your information from unauthorized access,
          alteration, disclosure, or destruction. However, no method of transmission or storage is
          completely secure, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="7. Your Rights">
        <p>
          Depending on your jurisdiction, you may have the right to access, correct, export, or
          delete your personal information. To exercise these rights, please contact us using the
          details below.
        </p>
      </Section>

      <Section title="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Changes will be reflected by updating
          the "Last updated" date above, and continued use of the Service constitutes acceptance of
          the revised policy.
        </p>
      </Section>

      <Section title="9. Contact">
        <p>
          If you have questions or concerns about this Privacy Policy, please contact us at{' '}
          <a
            href="mailto:support@needfulthings.com"
            className="text-foreground underline underline-offset-4"
          >
            support@needfulthings.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}
